/**
 * @name CarrinhoView
 * @author Keven Adan Bezerra | kevenadanb
 * @description Visualização do carrinho de compras com resumo e confirmação
 * @package src/components/pages/CarrinhoView.tsx
 */

import {
    Add as AddIcon,
    BlockOutlined as BlockIcon,
    ShoppingCartCheckout as CheckoutIcon,
    Remove as RemoveIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

import ProdutoImagem from "@/components/common/ProdutoImagem";
import { useCarrinho } from "@/contexts/CarrinhoContext";
import { usePedido } from "@/contexts/PedidoContext";
import { useUserContext } from "@/contexts/UserContext";
import { useTotemServices } from "@/services/supabase/entities/useTotemServices";
import useThemeContext from "@/themes/hooks/useThemeContext";
import { getGlassButton, getGlassCard } from "@/themes/styles/glassStyles";

interface CarrinhoViewProps {
  onVoltarProdutos: () => void;
  onConfirmarPedido: () => void;
}

const CarrinhoView: React.FC<CarrinhoViewProps> = ({
  onVoltarProdutos,
  onConfirmarPedido,
}) => {
  const { theme } = useThemeContext();
  const { alunoAtual } = useUserContext();
  const { itens, total, adicionarItem, removerItem, validarSaldo } =
    useCarrinho();
  const { confirmarPedido, isProcessandoPedido } = usePedido();
  const [confirmandoPedido, setConfirmandoPedido] = useState(false);

  const totemServices = useTotemServices();
  const confirmarPedidoRef = useRef(confirmarPedido);
  const verificarProdutosRef = useRef(totemServices.restricoes.verificarProdutos);
  useEffect(() => {
    confirmarPedidoRef.current = confirmarPedido;
  }, [confirmarPedido]);

  useEffect(() => {
    verificarProdutosRef.current = totemServices.restricoes.verificarProdutos;
  }, [totemServices.restricoes.verificarProdutos]);

  const [idsRestringidos, setIdsRestringidos] = useState<Set<number>>(
    new Set(),
  );
  const [verificandoRestricoes, setVerificandoRestricoes] = useState(false);

  const produtoIdsNoCarrinho = useMemo(
    () => itens.map((i) => i.id),
    [itens],
  );
  const assinaturaItens = useMemo(
    () =>
      itens
        .map((i) => `${i.id}:${i.quantidade}`)
        .sort()
        .join("|"),
    [itens],
  );

  useEffect(() => {
    if (!alunoAtual?.id || itens.length === 0) {
      setIdsRestringidos(new Set());
      return;
    }
    // Evita loop: só verifica se não está já verificando
    if (verificandoRestricoes) return;

    let cancelado = false;
    setVerificandoRestricoes(true);
    verificarProdutosRef
      .current(alunoAtual.id, produtoIdsNoCarrinho)
      .then((resultado) => {
        if (cancelado) return;
        if (!resultado.permitido && resultado.restricoes.length > 0) {
          const ids = new Set<number>(
            resultado.restricoes
              .map((r) => r.fk_id_produto)
              .filter((id): id is number => id != null),
          );
          setIdsRestringidos(ids);
        } else {
          setIdsRestringidos(new Set());
        }
      })
      .catch(() => {
        // Fail-closed real: bloqueia todos os itens do carrinho quando a verificação falha
        if (!cancelado) {
          setIdsRestringidos(new Set(itens.map((i) => i.id)));
          setVerificandoRestricoes(false);
        }
      })
      .finally(() => {
        if (!cancelado) setVerificandoRestricoes(false);
      });
    return () => {
      cancelado = true;
    };
  }, [alunoAtual?.id, assinaturaItens, produtoIdsNoCarrinho, itens]);

  const handleConfirmarPedido = async () => {
    // 🛡️ Prevenir cliques duplos com debounce temporal
    const agora = Date.now();
    const ultimoClique = (window as any)._ultimoCliqueConfirmacao || 0;
    const tempoDebounce = 2000; // 2 segundos

    if (agora - ultimoClique < tempoDebounce) {
      console.warn("🚫 Clique muito rápido ignorado (debounce)", {
        tempoDecorrido: agora - ultimoClique,
        tempoMinimo: tempoDebounce,
      });
      return;
    }

    (window as any)._ultimoCliqueConfirmacao = agora;

    // 🛡️ Prevenir múltiplas execuções
    if (confirmandoPedido || isProcessandoPedido) {
      console.warn("🚫 Pedido já está sendo confirmado, ignorando clique");
      return;
    }

    setConfirmandoPedido(true);
    try {
      if (idsRestringidos.size > 0) {
        const nomes = itens
          .filter((it) => idsRestringidos.has(it.id))
          .map((it) => it.nome)
          .join(", ");
        console.warn(
          "⚠️ Removendo itens com restrição alimentar antes de confirmar:",
          nomes,
        );

        const itensRestantesCount = itens.filter(
          (it) => !idsRestringidos.has(it.id),
        ).length;
        flushSync(() => {
          idsRestringidos.forEach((id) => removerItem(id));
          setIdsRestringidos(new Set());
        });

        if (itensRestantesCount === 0) {
          return;
        }
      }

      const sucesso = await confirmarPedidoRef.current();
      if (sucesso) {
        onConfirmarPedido();
      }
    } finally {
      setConfirmandoPedido(false);
    }
  };

  const itensValidos = itens.filter((it) => !idsRestringidos.has(it.id));
  const podeConfirmar =
    itens.length > 0 &&
    itensValidos.length > 0 &&
    validarSaldo(alunoAtual?.saldo || 0) &&
    !confirmandoPedido &&
    !isProcessandoPedido &&
    !verificandoRestricoes;
  const saldoInsuficiente = !validarSaldo(alunoAtual?.saldo || 0);
  const estaProcessando = confirmandoPedido || isProcessandoPedido;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          ...getGlassCard(theme, "medium"),
          background: `linear-gradient(135deg, ${theme.palette.primary.main}40, ${theme.palette.primary.dark}20)`,
          color: theme.palette.text.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          borderRadius: 0,
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Seu Carrinho ({itens.length} {itens.length === 1 ? "item" : "itens"})
        </Typography>
      </Box>

      {/* Conteúdo */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        {itens.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              🛒
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center">
              Seu carrinho está vazio
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 3 }}
            >
              Adicione produtos para continuar
            </Typography>

            {/* Botão fixo no final */}
            <Box sx={{ mt: "auto", pt: 2, width: "100%" }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onVoltarProdutos}
                size="large"
                sx={{
                  height: 56,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  ...getGlassButton(theme, "primary"),
                }}
              >
                Ver Produtos
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* Lista de Itens - Área com scroll */}
            {verificandoRestricoes && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  color: "text.secondary",
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption">
                  Verificando restrições alimentares...
                </Typography>
              </Box>
            )}
            {idsRestringidos.size > 0 && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: 1 }}
                action={
                  <Button
                    color="error"
                    size="small"
                    variant="contained"
                    onClick={() => {
                      idsRestringidos.forEach((id) => removerItem(id));
                      setIdsRestringidos(new Set());
                    }}
                  >
                    Remover agora
                  </Button>
                }
              >
                <Typography variant="body2" fontWeight="bold">
                  {idsRestringidos.size} item(s) com restrição alimentar
                </Typography>
                <Typography variant="caption" display="block">
                  Serão removidos automaticamente ao confirmar o pedido.
                </Typography>
              </Alert>
            )}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                mb: 2,
                minHeight: 0,
                maxHeight: "45vh", // Limitar altura para garantir espaço para o botão
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.palette.grey[100],
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.grey[400],
                  borderRadius: "4px",
                  "&:hover": {
                    background: theme.palette.grey[600],
                  },
                },
              }}
            >
              <Card sx={{ mb: 2, ...getGlassCard(theme, "light") }}>
                <List>
                  {itens.map((item, index) => {
                    const estaRestringido = idsRestringidos.has(item.id);
                    return (
                      <React.Fragment key={item.id}>
                        <ListItem
                          sx={{
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            ...(estaRestringido && {
                              bgcolor: `${theme.palette.error.light}33`,
                              borderLeft: `4px solid ${theme.palette.error.main}`,
                              borderRadius: 1,
                              opacity: 0.75,
                            }),
                          }}
                        >
                          {/* Imagem do Produto */}
                          <ProdutoImagem
                            src={item.imagem}
                            alt={item.nome}
                            sx={{
                              width: { xs: 60, sm: 70 },
                              height: { xs: 60, sm: 70 },
                              borderRadius: 1,
                              mr: 2,
                              bgcolor: "#f0f0f0",
                              flexShrink: 0,
                            }}
                          />

                          {/* Informações do Produto */}
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  sx={
                                    estaRestringido
                                      ? {
                                          textDecoration: "line-through",
                                          color: "text.disabled",
                                        }
                                      : {}
                                  }
                                >
                                  {item.nome}
                                </Typography>
                                {estaRestringido && (
                                  <Chip
                                    label="Restrito"
                                    size="small"
                                    color="error"
                                    icon={<BlockIcon />}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.65rem",
                                      fontWeight: "bold",
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body1"
                                color="primary"
                                fontWeight="bold"
                              >
                                R$ {item.preco.toFixed(2)}
                              </Typography>
                            }
                            sx={{ flex: 1 }}
                          />

                          {/* Controles de Quantidade */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              onClick={() => removerItem(item.id)}
                              sx={{
                                bgcolor: theme.palette.error.main,
                                color: "white",
                                width: 40,
                                height: 40,
                                "&:hover": {
                                  bgcolor: theme.palette.error.dark,
                                },
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>

                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ minWidth: 40, textAlign: "center" }}
                            >
                              {item.quantidade}
                            </Typography>

                            <IconButton
                              onClick={() =>
                                !estaRestringido && adicionarItem(item)
                              }
                              disabled={estaRestringido}
                              aria-label={`Adicionar mais ${item.nome}`}
                              sx={{
                                bgcolor: estaRestringido
                                  ? theme.palette.action.disabledBackground
                                  : theme.palette.primary.main,
                                color: "white",
                                width: 40,
                                height: 40,
                                "&:hover": {
                                  bgcolor: estaRestringido
                                    ? theme.palette.action.disabledBackground
                                    : theme.palette.primary.dark,
                                },
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>

                          {/* Subtotal */}
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ ml: 2, minWidth: 80, textAlign: "right" }}
                          >
                            R$ {(item.preco * item.quantidade).toFixed(2)}
                          </Typography>
                        </ListItem>

                        {index < itens.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Card>
            </Box>

            {/* Resumo e Botão de Confirmação - Fixo na parte inferior */}
            <Box sx={{ flexShrink: 0, maxHeight: "50vh", overflow: "auto" }}>
              <Card sx={{ ...getGlassCard(theme, "medium") }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Resumo do Pedido
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1">Subtotal:</Typography>
                    <Typography variant="body1">
                      R$ {total.toFixed(2)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Total:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      R$ {total.toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Informações do Saldo */}
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Saldo atual:</Typography>
                      <Typography variant="body2">
                        R$ {alunoAtual?.saldo?.toFixed(2) || "0,00"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">
                        Saldo após compra:
                      </Typography>
                      <Typography
                        variant="body2"
                        color={saldoInsuficiente ? "error" : "success.main"}
                      >
                        R$ {((alunoAtual?.saldo || 0) - total).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Mensagem de Saldo Insuficiente */}
                  {saldoInsuficiente && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: theme.palette.error.light,
                        color: theme.palette.error.contrastText,
                        borderRadius: 1,
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        ⚠️ Saldo insuficiente para finalizar a compra
                      </Typography>
                    </Box>
                  )}

                  {/* Botão de Confirmação */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<CheckoutIcon />}
                    onClick={handleConfirmarPedido}
                    disabled={!podeConfirmar}
                    sx={{
                      ...getGlassButton(theme, "primary"),
                      height: 60,
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      mt: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {estaProcessando ? "Processando..." : "Confirmar Pedido"}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CarrinhoView;
